import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import type { ActionKind, AnyScreenAction, ScreenManifest, ScreenRegistry } from '@/shared/screen';

/** Como o chat do Luy chama uma ação de tela rodando e terminada (U56), já traduzido. */
export interface AgentActionLabels {
  readonly label: string;
  readonly kind: ActionKind;
  readonly running: string;
  readonly done: string;
  /** Terminada sem achar nada (`result: []`). */
  readonly empty?: string;
}

/** O que o chat precisa para nomear as ferramentas de tela sem mostrar o nome técnico. */
export interface AgentLabels {
  screenTitle(screenId: string): string | undefined;
  action(actionId: string): AgentActionLabels | undefined;
}

const FILTER = { running: msg`Aplicando filtro`, done: msg`Filtro aplicado` };

function labelsOf(i18n: I18n, action: AnyScreenAction): AgentActionLabels {
  const label = i18n._(action.label);
  const activity = action.activity;
  if (activity)
    return { label, kind: action.kind, running: i18n._(activity.running), done: i18n._(activity.done), ...(activity.empty ? { empty: i18n._(activity.empty) } : {}) };
  // Ação de página é o filtro da lista (08 §4.2); as outras, sem `activity`, levam o próprio rótulo.
  if (action.page === true) return { label, kind: action.kind, running: i18n._(FILTER.running), done: i18n._(FILTER.done) };
  return { label, kind: action.kind, running: label, done: label };
}

/** Os rótulos a partir do registro de telas e do que as telas montadas registraram agora. */
export function agentLabels(deps: { readonly screens: ScreenRegistry; readonly mounted: ReadonlyMap<string, ScreenManifest>; readonly i18n: I18n }): AgentLabels {
  const find = (actionId: string): AnyScreenAction | undefined => {
    for (const screen of deps.mounted.values()) {
      const found = screen.actions.find((action) => action.id === actionId);
      if (found) return found;
    }
    for (const screen of deps.screens.screens) {
      const found = screen.actions.find((action) => action.id === actionId);
      if (found) return found;
    }
    return undefined;
  };
  return {
    screenTitle: (screenId) => {
      const screen = deps.screens.byId(screenId);
      return screen ? deps.i18n._(screen.title) : undefined;
    },
    action: (actionId) => {
      const found = find(actionId);
      return found ? labelsOf(deps.i18n, found) : undefined;
    },
  };
}

/** Sem o provider (prévia, teste isolado do chat): nada é achado e o chat usa o nome genérico. */
export const NO_AGENT_LABELS: AgentLabels = { screenTitle: () => undefined, action: () => undefined };
